import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { AgencyRawDataStorePayload } from "../../../../Schema/agencyRawData.types";

export type AgencyRawDataStoreLoadAction = {
  type: string;
  payload: AgencyRawDataStorePayload;
};

export type AgencyRawDataStoreData = {
  data: {
    message?: string;
    status?: string;
  };
};

const AgencyRawDataStore = new ReduxFetchState<
  AgencyRawDataStoreData | null,
  AgencyRawDataStorePayload,
  any
>("agencyRawDataStore");

const RESET_AGENCY_RAW_DATA_STORE = "RESET_AGENCY_RAW_DATA_STORE";

export function* watchAgencyRawDataStore(action: AgencyRawDataStoreLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();
    const { id, title, slug, raw_content } = action.payload;

    const body: Record<string, string> = { raw_content };
    if (title) body.title = title;
    if (slug) body.slug = slug;

    const data: AgencyRawDataStoreData = yield axios({
      url: `api/v1/ai/agency/${id}/raw-data/store`,
      method: "POST",
      headers: { lang },
      data: body,
    });

    yield alert({
      message: data.data.message ?? "دانش جدید با موفقیت افزوده شد.",
      type: "success",
    });
    yield put(
      AgencyRawDataStore.actions.loadSuccess({
        data: { ...data.data, status: "success" },
      }),
    );
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? e?.response?.data?.message ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(AgencyRawDataStore.actions.loadFailure(e.message));
  }
}

export function* agencyRawDataStoreSagas() {
  yield takeEvery(AgencyRawDataStore.actionTypes.load, watchAgencyRawDataStore);
}

const initialState = AgencyRawDataStore.reducer(undefined, { type: "@@INIT" });

const customReducer = (state = initialState, action: any) => {
  if (action.type === RESET_AGENCY_RAW_DATA_STORE) {
    return initialState;
  }
  return AgencyRawDataStore.reducer(state, action);
};

export function useAgencyRawDataStore() {
  const { loading, data, ...agencyRawDataStoreState } = useAppSelector(
    (state) => state.agencyRawDataStore,
  );
  const dispatch = useAppDispatch();

  const dispatchAgencyRawDataStore = useCallback(
    (payload: AgencyRawDataStorePayload) => {
      dispatch(AgencyRawDataStore.actions.load(payload));
    },
    [dispatch],
  );

  const clearAgencyRawDataStore = useCallback(() => {
    dispatch({ type: RESET_AGENCY_RAW_DATA_STORE });
  }, [dispatch]);

  return {
    ...agencyRawDataStoreState,
    dispatchAgencyRawDataStore,
    clearAgencyRawDataStore,
    agencyRawDataStoreLoading: loading,
    agencyRawDataStoreData: data,
  };
}

export const {
  actionTypes: agencyRawDataStoreActionsTypes,
  actions: agencyRawDataStoreActions,
} = AgencyRawDataStore;

export const agencyRawDataStoreReducer = customReducer;